/**
 * Schema.org Generators for Soloway.lk
 * Fully compliant with Google Rich Results & Schema.org standards
 */

const BASE_URL = "https://soloway.lk";

/**
 * 1. WebSite Schema with Sitelinks SearchBox Action
 * Triggers Google Sitelinks SearchBox in Search Results
 */
export const getWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${BASE_URL}/#website`,
  "name": "Soloway - Sri Lanka Travel, Hotels & Local Guides",
  "alternateName": ["Soloway.lk", "Soloway Sri Lanka Tourism"],
  "url": BASE_URL,
  "inLanguage": "en",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${BASE_URL}/sri-lanka-hotels?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  },
  "publisher": {
    "@id": `${BASE_URL}/#organization`
  }
});

/**
 * 2. Organization Schema
 * Establishes Soloway as the entity for Knowledge Panels
 */
export const getOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${BASE_URL}/#organization`,
  "name": "SoloWay",
  "legalName": "SOLOWAY.LK (PVT) LTD",
  "identifier": "PV 00373363",
  "foundingDate": "2026-09-06",
  "url": BASE_URL,
  "logo": {
    "@type": "ImageObject",
    "url": `${BASE_URL}/logo.png`,
    "caption": "Soloway Sri Lanka Logo",
    "width": 512,
    "height": 512
  },
  "image": `${BASE_URL}/logo.png`,
  "sameAs": [
    "https://www.facebook.com/soloway.lk",
    "https://www.instagram.com/soloway.lk",
    "https://www.linkedin.com/company/solowaylk",
    "https://twitter.com/solowaylk",
    "https://www.youtube.com/@solowaylk"
  ],
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "email": "support@soloway.lk",
      "contactType": "customer service",
      "areaServed": "LK",
      "availableLanguage": ["English", "Sinhala", "Tamil"]
    }
  ],
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Colombo",
    "addressRegion": "Western Province",
    "addressCountry": "LK"
  }
});

/**
 * 3. TravelAgency / LocalBusiness Schema
 * Tailored for Sri Lankan Tourism Services
 */
export const getTravelAgencySchema = () => ({
  "@context": "https://schema.org",
  "@type": "TravelAgency",
  "@id": `${BASE_URL}/#travelagency`,
  "name": "SoloWay Sri Lanka Travel & Tourism Platform",
  "legalName": "SOLOWAY.LK (PVT) LTD",
  "identifier": "PV 00373363",
  "foundingDate": "2026-09-06",
  "image": `${BASE_URL}/logo.png`,
  "url": BASE_URL,
  "email": "support@soloway.lk",
  "priceRange": "$$",
  "currenciesAccepted": "USD, LKR, EUR, GBP",
  "paymentAccepted": "Credit Card, Debit Card, Payments.lk, Koko",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Colombo",
    "addressRegion": "Western Province",
    "addressCountry": "LK"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 6.9271,
    "longitude": 79.8612
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday"
      ],
      "opens": "00:00",
      "closes": "23:59"
    }
  ],
  "areaServed": [
    { "@type": "AdministrativeArea", "name": "Sri Lanka" },
    { "@type": "City", "name": "Colombo" },
    { "@type": "City", "name": "Kandy" },
    { "@type": "City", "name": "Galle" },
    { "@type": "City", "name": "Ella" },
    { "@type": "City", "name": "Sigiriya" },
    { "@type": "City", "name": "Mirissa" },
    { "@type": "City", "name": "Nuwara Eliya" },
    { "@type": "City", "name": "Arugam Bay" },
    { "@type": "City", "name": "Yala" }
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Soloway Sri Lanka Travel Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Sri Lanka Hotel Bookings",
          "description": "Direct bookings with verified Sri Lankan hotels, boutique villas, and resorts."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Certified Sri Lanka Tour Guides",
          "description": "Book verified local tour guides with multilingual capabilities."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Sri Lanka Visa Assistance & Excursions",
          "description": "Fast ETA tourist visa consulting and customized island-wide day tours."
        }
      }
    ]
  }
});

/**
 * 4. Dynamic BreadcrumbList Schema
 * @param {Array<{name: string, url: string}>} items
 */
export const getBreadcrumbSchema = (items = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": BASE_URL
    },
    ...items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 2,
      "name": item.name,
      "item": item.url.startsWith("http") ? item.url : `${BASE_URL}${item.url.startsWith("/") ? item.url : `/${item.url}`}`
    }))
  ]
});

/**
 * 5. Hotel / LodgingBusiness Schema
 */
export const getHotelSchema = ({
  id,
  name,
  description,
  city,
  address,
  image,
  priceRange = "$$",
  minPrice = 45,
  maxPrice = 350,
  currency = "USD",
  ratingValue = null,
  reviewCount = null,
  amenities = ["Free WiFi", "Swimming Pool", "Air Conditioning"],
  latitude = 6.8728,
  longitude = 81.0463
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Hotel",
    "@id": `${BASE_URL}/sri-lanka-hotels/${city ? city.toLowerCase() : "stay"}/${id}#hotel`,
    "name": name,
    "description": description,
    "url": `${BASE_URL}/sri-lanka-hotels/${city ? city.toLowerCase() : "stay"}/${id}`,
    "image": image ? (image.startsWith("http") ? image : `${BASE_URL}${image}`) : `${BASE_URL}/logo.png`,
    "priceRange": priceRange,
    "currenciesAccepted": currency,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": address || `${city || "Sri Lanka"}`,
      "addressLocality": city || "Colombo",
      "addressRegion": "Western Province",
      "addressCountry": "LK"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": latitude,
      "longitude": longitude
    },
    "amenityFeature": (amenities || []).map(amenity => ({
      "@type": "LocationFeatureSpecification",
      "name": amenity,
      "value": "True"
    })),
    "makesOffer": {
      "@type": "Offer",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "minPrice": minPrice,
        "maxPrice": maxPrice,
        "priceCurrency": currency
      },
      "availability": "https://schema.org/InStock",
      "validFrom": new Date().toISOString().split("T")[0]
    }
  };

  if (ratingValue && reviewCount && Number(reviewCount) > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": ratingValue.toString(),
      "bestRating": "5",
      "worstRating": "1",
      "ratingCount": reviewCount.toString()
    };
  }

  return schema;
};

/**
 * 6. Guide / Person Schema
 */
export const getGuideSchema = ({
  id,
  name,
  bio,
  languages = ["English", "Sinhala"],
  ratingValue = null,
  reviewCount = null,
  credentialName = null,
  image,
  hourlyRate = 25,
  locations = ["Ella", "Kandy", "Sigiriya"]
}) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${BASE_URL}/sri-lanka-guides/${id}#guide`,
    "name": name,
    "jobTitle": "Local Sri Lanka Tour Guide & Chauffeur",
    "description": bio,
    "image": image ? (image.startsWith("http") ? image : `${BASE_URL}${image}`) : `${BASE_URL}/logo.png`,
    "url": `${BASE_URL}/sri-lanka-guides/${id}`,
    "knowsLanguage": languages,
    "areaServed": (locations || []).map(loc => ({ "@type": "AdministrativeArea", "name": loc })),
    "offers": {
      "@type": "Offer",
      "price": hourlyRate.toString(),
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "description": `Private guided tour service across ${(locations || []).join(", ")}`
    }
  };

  if (credentialName) {
    schema.hasCredential = {
      "@type": "EducationalOccupationalCredential",
      "name": credentialName
    };
  }

  if (ratingValue && reviewCount && Number(reviewCount) > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      "ratingValue": ratingValue.toString(),
      "reviewCount": reviewCount.toString(),
      "bestRating": "5"
    };
  }

  return schema;
};

/**
 * 7. TouristAttraction Schema
 */
export const getTouristAttractionSchema = ({
  id,
  name,
  description,
  locationName = "Sigiriya, Matale District",
  image,
  latitude = 7.9570,
  longitude = 80.7603,
  touristType = ["Adventure Tourist", "Cultural Tourist", "Eco Tourist"]
}) => ({
  "@context": "https://schema.org",
  "@type": "TouristAttraction",
  "@id": `${BASE_URL}/most-famous-places-sri-lanka/${id}#attraction`,
  "name": name,
  "description": description,
  "url": `${BASE_URL}/most-famous-places-sri-lanka/${id}`,
  "image": image ? (image.startsWith("http") ? image : `${BASE_URL}${image}`) : `${BASE_URL}/logo.png`,
  "isAccessibleForFree": false,
  "touristType": touristType,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": locationName,
    "addressCountry": "LK"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": latitude,
    "longitude": longitude
  },
  "publicAccess": true
});
